import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type {
    BariPostNurseSpecialistFollowUpBeoordelingEnVervolgReadLabelBase,
    BariPostNurseSpecialistFollowUpComorbiditeitenReadLabelBase,
    BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadLabelBase,
    BariPostNurseSpecialistFollowUpReadSectionTitleBase,
} from '../bariPostNurseSpecialistFollowUpReadSectionsBase';
import type {
    BariPostNurseSpecialistFollowUpBeoordelingEnVervolgDataV2,
    BariPostNurseSpecialistFollowUpComorbiditeitenDataV2,
    BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieDataV2,
} from './bariPostNurseSpecialistFollowUpDataV2';
import { beoordelingEnVervolgBase } from '../bariPostNurseSpecialistFollowUpReadSectionsBase';

type BariPostNurseSpecialistFollowUpComorbiditeitenReadKeyV2 =
    | keyof Exclude<BariPostNurseSpecialistFollowUpComorbiditeitenDataV2, 'dumpingklachten_options'>
    | 'late_dumping'
    | 'vroege_dumping';

type BariPostNurseSpecialistFollowUpComorbiditeitenReadLabelV2 =
    | BariPostNurseSpecialistFollowUpComorbiditeitenReadLabelBase
    | 'Status depressie'
    | 'Status longziekte'
    | 'Start adjuvante gewichtsreducerende medicatie'
    | 'Medicatie'
    | 'Startdatum medicatie'
    | 'Stopdatum medicatie';

type BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadKeyV2 =
    keyof BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieDataV2;

type BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadLabelV2 =
    | BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadLabelBase
    | 'Tabak gebruik'
    | 'Toelichting tabak gebruik';

type BariPostNurseSpecialistFollowUpBeoordelingEnVervolgReadKeyV2 =
    keyof BariPostNurseSpecialistFollowUpBeoordelingEnVervolgDataV2;

type BariPostNurseSpecialistFollowUpBeoordelingEnVervolgReadLabelV2 =
    BariPostNurseSpecialistFollowUpBeoordelingEnVervolgReadLabelBase;

export type BariPostNurseSpecialistFollowUpReadSectionTitleV2 = BariPostNurseSpecialistFollowUpReadSectionTitleBase;

const comorbiditeitenV2: TypedReadSchema<
    BariPostNurseSpecialistFollowUpComorbiditeitenReadKeyV2,
    BariPostNurseSpecialistFollowUpComorbiditeitenReadLabelV2,
    Extract<BariPostNurseSpecialistFollowUpReadSectionTitleV2, 'Comorbiditeiten'>
> = {
    items: [
        { key: 'hypertensie', label: 'Hypertensie' },
        {
            key: 'hypertensie_medicamenteuze_veranderingen',
            label: 'Medicamenteuze veranderingen',
        },
        {
            key: 'hypertensie_medicamenteuze_veranderingen_toelichting',
            label: 'Toelichting medicamenteuze veranderingen',
        },
        { key: 'diabetes_mellitus', label: 'Diabetes mellitus' },
        {
            key: 'diabetes_mellitus_medicamenteuze_veranderingen',
            label: 'Medicamenteuze veranderingen',
        },
        {
            key: 'diabetes_mellitus_medicamenteuze_veranderingen_toelichting',
            label: 'Toelichting medicamenteuze veranderingen',
        },
        { append: 'mmol/mol', key: 'hba1c', label: 'HbA1c', type: 'number' },
        { key: 'dyslipidemie', label: 'Dyslipidemie' },
        {
            key: 'dyslipidemie_medicamenteuze_veranderingen',
            label: 'Medicamenteuze veranderingen',
        },
        {
            key: 'dyslipidemie_medicamenteuze_veranderingen_toelichting',
            label: 'Toelichting medicamenteuze veranderingen',
        },
        { key: 'reflux_klachten', label: 'Klachten van reflux', type: 'boolean' },
        { key: 'reflux_klachten_toelichting', label: 'Toelichting reflux' },
        { key: 'gastroesophageal_reflux_disease', label: 'GERD' },
        {
            key: 'gastroesophageal_reflux_disease_medicamenteuze_veranderingen',
            label: 'Medicamenteuze veranderingen',
        },
        {
            key: 'gastroesophageal_reflux_disease_medicamenteuze_veranderingen_toelichting',
            label: 'Toelichting medicamenteuze veranderingen',
        },
        { key: 'osas', label: 'OSAS' },
        {
            key: 'osas_therapeutische_veranderingen',
            label: 'Therapeutische veranderingen',
        },
        {
            key: 'osas_therapeutische_veranderingen_toelichting',
            label: 'Toelichting therapeutische veranderingen',
        },
        { key: 'gewrichtsklachten', label: 'Gewrichtsklachten' },
        {
            key: 'gewrichtsklachten_therapeutische_veranderingen',
            label: 'Therapeutische veranderingen',
        },
        {
            key: 'gewrichtsklachten_therapeutische_veranderingen_toelichting',
            label: 'Toelichting therapeutische veranderingen',
        },
        {
            key: 'depressie_status',
            label: 'Status depressie',
        },
        {
            key: 'longziekte_status',
            label: 'Status longziekte',
        },
        {
            key: 'start_adjuvante_gewichtsreducerende_medicatie',
            label: 'Start adjuvante gewichtsreducerende medicatie',
            type: 'boolean',
        },
        { key: 'adjuvante_gewichtsreducerende_medicatie', label: 'Medicatie' },
        {
            key: 'adjuvante_gewichtsreducerende_medicatie_startdatum',
            label: 'Startdatum medicatie',
            type: 'dateAsString',
        },
        {
            key: 'adjuvante_gewichtsreducerende_medicatie_stopdatum',
            label: 'Stopdatum medicatie',
            type: 'dateAsString',
        },
        { key: 'dumpingklachten', label: 'Dumpingklachten', type: 'boolean' },
        { key: 'vroege_dumping', label: 'Vroege dumping', type: 'boolean' },
        { key: 'late_dumping', label: 'Late dumping', type: 'boolean' },
        { key: 'dumpingklachten_toelichting', label: 'Toelichting dumpingklachten' },
    ],
    sectionTitle: 'Comorbiditeiten',
};

const leefstijlEnMedicatieV2: TypedReadSchema<
    BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadKeyV2,
    BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadLabelV2,
    Extract<BariPostNurseSpecialistFollowUpReadSectionTitleV2, 'Leefstijl en medicatie'>
> = {
    items: [
        {
            key: 'beloop_aanvullende_anamnese',
            label: 'Anamnese (leefstijl, evt. klachten)',
        },
        {
            key: 'toelichting_deficienties_labuitslag',
            label: 'Toelichting deficiënties op basis van labuitslag',
        },
        { key: 'multivitaminen', label: 'Gebruik multivitaminen', type: 'boolean' },
        { key: 'multivitaminen_toelichting', label: 'Toelichting multivitaminegebruik' },
        { key: 'alcohol', label: 'Alcohol', type: 'boolean' },
        { append: 'per week', key: 'alcohol_aantal_eenheden_per_week', label: 'Aantal eenheden', type: 'number' },
        { key: 'alcohol_toelichting', label: 'Toelichting alcohol' },
        { key: 'tabak_gebruik', label: 'Tabak gebruik' },
        { key: 'tabak_toelichting', label: 'Toelichting tabak gebruik' },
        { key: 'medicatie', label: 'Gebruik medicatie', type: 'boolean' },
        { key: 'huidige_medicatie', label: 'Huidige medicatie' },
        { key: 'ontlasting', label: 'Ontlasting' },
        {
            append: 'keer per dag',
            key: 'ontlasting_frequentie_per_dag',
            label: 'Frequentie ontlasting',
            type: 'number',
        },
    ],
    sectionTitle: 'Leefstijl en medicatie',
};

const beoordelingEnVervolgV2 = beoordelingEnVervolgBase;

export type BariPostNurseSpecialistFollowUpReadKeyV2 =
    | BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadKeyV2
    | BariPostNurseSpecialistFollowUpComorbiditeitenReadKeyV2
    | BariPostNurseSpecialistFollowUpBeoordelingEnVervolgReadKeyV2;

export type BariPostNurseSpecialistFollowUpReadLabelV2 =
    | BariPostNurseSpecialistFollowUpLeefstijlEnMedicatieReadLabelV2
    | BariPostNurseSpecialistFollowUpComorbiditeitenReadLabelV2
    | BariPostNurseSpecialistFollowUpBeoordelingEnVervolgReadLabelV2;

export const bariPostNurseSpecialistFollowUpReadSectionsV2: TypedReadSchema<
    BariPostNurseSpecialistFollowUpReadKeyV2,
    BariPostNurseSpecialistFollowUpReadLabelV2,
    BariPostNurseSpecialistFollowUpReadSectionTitleV2
>[] = [leefstijlEnMedicatieV2, comorbiditeitenV2, beoordelingEnVervolgV2];
